// Модуль «Официанты» во вкладке «Устройства → Официанты»: карточка подключения
// (модуль не подключён), полоса статуса (подключён или дорабатывает срок) и
// подтверждения. Деньги — со следующего продления подписки, отключение — с
// конца оплаченного периода; суммы и даты считает бэкенд (/admin/modules/waiters).

import { Link } from 'react-router-dom';
import {
  ArrowLeftRight,
  CalendarClock,
  Check,
  CircleUserRound,
  CreditCard,
  MessageSquare,
  Printer,
  Send,
  ShieldCheck,
  Trash2,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import type { WaitersModuleStatus } from '@/services/admin';
import { formatRub } from '@/utils/format';

function dayMonth(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function perPeriod(s: WaitersModuleStatus, kop: number): string {
  return `${formatRub(kop)} ${s.billingPeriod === 'YEARLY' ? 'в год' : 'в месяц'}`;
}

/** Когда начнут брать деньги и какой станет подписка. */
function chargeNote(s: WaitersModuleStatus): string {
  const total = perPeriod(s, s.amountWithKopecks);
  if (!s.paidFrom) return `Добавится к следующей оплате подписки: ${total}.`;
  const until = dayMonth(s.paidFrom);
  return s.subscriptionStatus === 'TRIAL'
    ? `До ${until} бесплатно — идёт пробный период. Дальше подписка ${total}.`
    : `До ${until} бесплатно — этот период уже оплачен. Со следующего продления подписка ${total}.`;
}

/** До какого числа бесплатно — для подтверждения, где новая сумма уже названа. */
function freeUntilNote(s: WaitersModuleStatus): string {
  if (!s.paidFrom) return `${formatRub(s.pricePeriodKopecks)} добавятся к следующей оплате.`;
  const until = dayMonth(s.paidFrom);
  return s.subscriptionStatus === 'TRIAL'
    ? `До ${until} бесплатно — идёт пробный период.`
    : `До ${until} бесплатно — этот период уже оплачен.`;
}

const FEATURES: Array<{ icon: LucideIcon; text: string }> = [
  { icon: Send, text: 'Заказ у стола сразу появляется на кассе и уходит на кухню' },
  { icon: Wallet, text: 'Оплата вашими способами, перенос стола и смена официанта' },
  { icon: Printer, text: 'Пречек печатает касса — за ним не нужно идти' },
  { icon: ShieldCheck, text: 'Что можно официантам, решаете в «Правах ролей»' },
];

const PREVIEW_ACTIONS: Array<{ icon: LucideIcon; label: string; danger?: boolean }> = [
  { icon: CreditCard, label: 'Оплатить' },
  { icon: CircleUserRound, label: 'Сменить официанта' },
  { icon: ArrowLeftRight, label: 'Поменять стол' },
  { icon: MessageSquare, label: 'Комментарий' },
  { icon: Printer, label: 'Распечатать чек' },
  { icon: Trash2, label: 'Удалить', danger: true },
];

/** Меню стола из приложения официанта — то, за что платят, в одном кадре. */
function WaiterAppPreview() {
  return (
    <div className="flex h-full items-center justify-center px-8 py-10">
      <div className="w-[236px]">
        <div className="rounded-2xl bg-white py-1.5 shadow-[0_14px_32px_-14px_rgba(15,23,42,0.4)] ring-1 ring-slate-900/5">
          {PREVIEW_ACTIONS.map(({ icon: Icon, label, danger }) => (
            <div
              key={label}
              className={`flex items-center gap-3 px-4 py-[7px] text-[13px] ${
                danger ? 'mt-1 border-t border-slate-100 pt-2.5 text-rose-600' : 'text-slate-900'
              }`}
            >
              <Icon
                size={16}
                strokeWidth={1.75}
                className={danger ? 'text-rose-600' : 'text-[#E85624]'}
              />
              {label}
            </div>
          ))}
        </div>
        <div className="ml-8 mt-3 rounded-2xl bg-white p-3.5 shadow-[0_10px_26px_-12px_rgba(232,86,36,0.55)] ring-1 ring-[#E85624]/60">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[15px] font-semibold text-slate-900">Стол 3</span>
            <span className="text-[11px] text-slate-400">только что</span>
          </div>
          <div className="mt-0.5 text-[12px] font-semibold text-orange-700">Мой · №1496</div>
          <div className="mt-1 text-[12px] text-slate-500">Капучино, круассан</div>
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-[12px]">
            <span className="text-slate-400">4 гостя</span>
            <span className="font-semibold tabular-nums text-slate-900">420 ₽</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Модуль не подключён: что это, сколько стоит и когда начнут брать деньги. */
export function WaitersModulePromo({
  status,
  pending,
  onEnable,
}: {
  status: WaitersModuleStatus;
  pending: boolean;
  onEnable: () => void;
}) {
  const yearly = status.billingPeriod === 'YEARLY';
  return (
    <section className="card overflow-hidden" aria-labelledby="waiters-module-title">
      <div className="grid md:grid-cols-[minmax(0,1fr)_300px] lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="p-6 sm:p-8">
          <h2
            id="waiters-module-title"
            className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 text-balance"
          >
            Официанты принимают заказы с телефона
          </h2>
          <p className="mt-2 max-w-[56ch] text-sm leading-relaxed text-slate-600">
            Приложение <strong className="font-medium text-slate-800">Resstro Официант</strong> для
            iPhone и Android. Официант остаётся у стола, а касса и кухня видят заказ в ту же
            секунду.
          </p>

          <ul className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex gap-3 text-sm leading-snug text-slate-700">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                  <Icon size={15} strokeWidth={2} aria-hidden />
                </span>
                <span className="pt-1">{text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-7 flex flex-col gap-4 border-t border-slate-100 pt-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-3xl font-semibold tabular-nums tracking-tight text-slate-900">
                  {formatRub(status.priceMonthKopecks)}
                </span>
                <span className="text-sm text-slate-500">
                  в месяц к подписке{yearly ? ` · ${formatRub(status.pricePeriodKopecks)} за год` : ''}
                </span>
              </div>
              <p className="mt-1 max-w-[52ch] text-sm text-slate-600">{chargeNote(status)}</p>
            </div>
            {status.canManage ? (
              <button
                className="btn-primary shrink-0 px-5 py-2.5"
                onClick={onEnable}
                disabled={pending}
              >
                Подключить официантов
              </button>
            ) : (
              <p className="shrink-0 text-sm text-slate-500">Подключает владелец заведения</p>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Отключить можно в любой момент — модуль доработает оплаченный период. Права
            официантов —{' '}
            <Link
              to="/users?view=roles&role=WAITER"
              className="text-slate-700 underline underline-offset-2 hover:text-slate-900"
            >
              в «Правах ролей»
            </Link>
            .
          </p>
        </div>
        <div aria-hidden className="hidden border-l border-slate-100 bg-[#F2F2F7] md:block">
          <WaiterAppPreview />
        </div>
      </div>
    </section>
  );
}

/** Модуль работает: подключён (продлевается) или дорабатывает срок. */
export function WaitersModuleBar({
  status,
  pending,
  onEnable,
  onDisable,
}: {
  status: WaitersModuleStatus;
  pending: boolean;
  onEnable: () => void;
  onDisable: () => void;
}) {
  if (status.state === 'ending') {
    const until = status.endsAt ? dayMonth(status.endsAt) : '';
    return (
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
        <CalendarClock size={18} className="shrink-0 text-amber-600" aria-hidden />
        <p className="min-w-0 flex-1 text-amber-900">
          {status.free ? (
            <>
              Официанты работают бесплатно до <strong className="font-semibold">{until}</strong>.
              Чтобы приложение не отключилось, подключите модуль —{' '}
              {perPeriod(status, status.pricePeriodKopecks)} со следующего продления.
            </>
          ) : (
            <>
              Модуль «Официанты» отключён и работает до <strong className="font-semibold">{until}</strong>.
              После этого телефоны официантов перестанут принимать заказы.
            </>
          )}
        </p>
        {status.canManage ? (
          <button className="btn-primary shrink-0" onClick={onEnable} disabled={pending}>
            {status.free ? 'Подключить' : 'Подключить снова'}
          </button>
        ) : (
          <span className="shrink-0 text-amber-800">Подключает владелец</span>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-card">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
        <Check size={15} strokeWidth={2.25} aria-hidden />
      </span>
      <span className="font-medium text-slate-900">Модуль «Официанты» подключён</span>
      <span className="text-slate-500">
        {status.free ? 'бесплатно' : `${perPeriod(status, status.pricePeriodKopecks)} к подписке`}
      </span>
      {status.canManage && (
        <button
          className="btn-ghost ml-auto shrink-0 text-slate-600"
          onClick={onDisable}
          disabled={pending}
        >
          Отключить
        </button>
      )}
    </div>
  );
}

export function EnableWaitersModal({
  status,
  pending,
  onClose,
  onConfirm,
}: {
  status: WaitersModuleStatus;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const resuming = status.state === 'ending';
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={resuming ? 'Подключить официантов снова' : 'Подключить официантов'}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button className="btn-primary" onClick={onConfirm} disabled={pending}>
            {pending ? 'Подключаем…' : 'Подключить'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 text-sm">
        <dl className="rounded-lg bg-slate-50 px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-600">Подписка сейчас</dt>
            <dd className="tabular-nums text-slate-900">{perPeriod(status, status.amountWithoutKopecks)}</dd>
          </div>
          <div className="mt-1.5 flex items-baseline justify-between gap-3">
            <dt className="text-slate-600">С модулем «Официанты»</dt>
            <dd className="font-semibold tabular-nums text-slate-900">
              {perPeriod(status, status.amountWithKopecks)}
            </dd>
          </div>
        </dl>
        <p className="text-slate-600">{freeUntilNote(status)}</p>
        <p className="text-slate-600">
          {resuming
            ? 'Официанты продолжат работать без перерыва.'
            : 'Модуль заработает сразу — следующим шагом подключите телефон первого официанта.'}
        </p>
      </div>
    </Modal>
  );
}

export function DisableWaitersModal({
  status,
  pending,
  onClose,
  onConfirm,
}: {
  status: WaitersModuleStatus;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Отключить официантов"
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn-primary bg-rose-600 hover:bg-rose-700 active:bg-rose-800"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? 'Отключаем…' : 'Отключить'}
          </button>
        </>
      }
    >
      <div className="grid gap-3 text-sm text-slate-600">
        <p>
          {status.paidFrom ? (
            <>
              Официанты будут работать до <strong className="text-slate-900">{dayMonth(status.paidFrom)}</strong> —
              этот период уже оплачен.
            </>
          ) : (
            'Телефоны официантов перестанут принимать заказы сразу.'
          )}{' '}
          Заказы, продажи и сами официанты в «Пользователях» сохранятся.
        </p>
        {!status.free && (
          <p>
            Со следующего продления подписка — {perPeriod(status, status.amountWithoutKopecks)}.
          </p>
        )}
      </div>
    </Modal>
  );
}
